import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.service';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

interface BonusLead {
  id: number;
  phone: string;
  customer_name: string;
  manager_name: string;
  project_name: string;
  bonus: boolean;
  accept_deal: boolean;
  created_at: string;
  updated_at: string;
}

interface BonusMetrics {
  total_bonus_leads: number;
  bonus_leads_percentage: number;
  bonus_conversion_rate: number;
  bonus_revenue: number;
  bonus_by_manager: Array<{
    manager_name: string;
    bonus_count: number;
    conversion_rate: number;
  }>;
  bonus_by_project: Array<{
    project_name: string;
    bonus_count: number;
    conversion_rate: number;
  }>;
}

interface BonusNotification {
  type: 'new_bonus' | 'bonus_converted' | 'bonus_expired';
  message: string;
  lead_id: number;
  created_at: string;
}

export const BonusPage: React.FC = () => {
  const [bonusLeads, setBonusLeads] = useState<BonusLead[]>([]);
  const [metrics, setMetrics] = useState<BonusMetrics | null>(null);
  const [notifications, setNotifications] = useState<BonusNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    manager_name: '',
    project_name: '',
    date_from: '',
    date_to: '',
    limit: 50
  });
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Загрузка бонусных лидов
  const fetchBonusLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await apiService.get(`/leads/bonus?${params}`);
      setBonusLeads(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке бонусных лидов:', error);
      setNotification('Ошибка при загрузке бонусных лидов');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Загрузка метрик
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await apiService.get(`/analytics/bonus-metrics`);
      if (response.data.success) {
        setMetrics(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка при загрузке метрик:', error);
    }
  }, []);

  // Загрузка уведомлений
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiService.get(`/leads/bonus/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке уведомлений:', error);
    }
  }, []);

  // Обновление бонусного статуса
  const updateBonusStatus = async (leadId: number, bonus: boolean) => {
    try {
      await apiService.put(`/leads/${leadId}/bonus`, { bonus });
      setNotification(`Бонусный статус обновлен для лида ${leadId}`);
      fetchBonusLeads();
      fetchMetrics();
    } catch (error) {
      console.error('Ошибка при обновлении бонусного статуса:', error);
      setNotification('Ошибка при обновлении бонусного статуса');
    }
  };

  // Сброс всех бонусных статусов
  const resetBonusStatuses = async () => {
    if (!window.confirm('Вы уверены, что хотите сбросить все бонусные статусы?')) {
      return;
    }

    try {
      const response = await apiService.post(`/leads/bonus/reset`);
      setNotification(`Сброшено ${response.data.reset_count} бонусных статусов`);
      fetchBonusLeads();
      fetchMetrics();
    } catch (error) {
      console.error('Ошибка при сбросе бонусных статусов:', error);
      setNotification('Ошибка при сбросе бонусных статусов');
    }
  };

  // Автоматическое назначение бонусов
  const autoAssignBonuses = async () => {
    try {
      const response = await apiService.post(`/leads/bonus/auto-assign`);
      setNotification(`Автоматически назначено ${response.data.assigned_count} бонусов`);
      fetchBonusLeads();
      fetchMetrics();
    } catch (error) {
      console.error('Ошибка при автоматическом назначении бонусов:', error);
      setNotification('Ошибка при автоматическом назначении бонусов');
    }
  };

  // Выбор/отмена выбора всех лидов
  const toggleSelectAll = () => {
    if (selectedLeads.length === bonusLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(bonusLeads.map(lead => lead.id));
    }
  };

  // Выбор/отмена выбора одного лида
  const toggleSelectLead = (leadId: number) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Массовое обновление бонусных статусов
  const bulkUpdateBonusStatus = async (bonus: boolean) => {
    if (selectedLeads.length === 0) {
      setNotification('Выберите лиды для обновления');
      return;
    }

    try {
      for (const leadId of selectedLeads) {
        await apiService.put(`/leads/${leadId}/bonus`, { bonus });
      }
      setNotification('Бонусные статусы обновлены');
      setSelectedLeads([]);
      fetchBonusLeads();
      fetchMetrics();
    } catch (error) {
      console.error('Ошибка при массовом обновлении бонусных статусов:', error);
      setNotification('Ошибка при массовом обновлении бонусных статусов');
    }
  };

  useEffect(() => {
    fetchBonusLeads();
    fetchMetrics();
    fetchNotifications();
  }, [fetchBonusLeads, fetchMetrics, fetchNotifications]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Управление бонусными лидами
      </Typography>

      {/* Уведомления */}
      {notification && (
        <Alert 
          severity={notification.includes('Ошибка') ? 'error' : 'info'} 
          onClose={() => setNotification(null)}
          sx={{ mb: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setNotification(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {notification}
        </Alert>
      )}

      {/* Метрики */}
      {metrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Метрики бонусных лидов
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' }, gap: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {metrics.total_bonus_leads}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Всего
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {typeof metrics.bonus_leads_percentage === 'number' ? metrics.bonus_leads_percentage.toFixed(1) : '0.0'}%
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  % от всех
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {typeof metrics.bonus_conversion_rate === 'number' ? metrics.bonus_conversion_rate.toFixed(1) : '0.0'}%
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Конверсия
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h6" : "h4"} color="white">
                  {typeof metrics.bonus_revenue === 'number' ? metrics.bonus_revenue.toLocaleString() : '0'} ₽
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Доход
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Действия */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Действия
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            <Button 
              variant="contained"
              color="primary"
              onClick={autoAssignBonuses}
              fullWidth={isMobile}
            >
              Авто-назначение бонусов
            </Button>
            <Button 
              variant="contained"
              color="error"
              onClick={resetBonusStatuses}
              fullWidth={isMobile}
            >
              Сбросить все бонусы
            </Button>
            {selectedLeads.length > 0 && (
              <>
                <Button 
                  variant="contained"
                  color="success"
                  onClick={() => bulkUpdateBonusStatus(true)}
                  fullWidth={isMobile}
                >
                  Назначить бонус ({selectedLeads.length})
                </Button>
                <Button 
                  variant="contained"
                  color="warning"
                  onClick={() => bulkUpdateBonusStatus(false)}
                  fullWidth={isMobile}
                >
                  Убрать бонус ({selectedLeads.length})
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Фильтры
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField
              label="Менеджер"
              value={filters.manager_name}
              onChange={(e) => setFilters(prev => ({ ...prev, manager_name: e.target.value }))}
              size="small"
              fullWidth={isMobile}
            />
            <TextField
              label="Проект"
              value={filters.project_name}
              onChange={(e) => setFilters(prev => ({ ...prev, project_name: e.target.value }))}
              size="small"
              fullWidth={isMobile}
            />
            <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
              <TextField
                label="Дата от"
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Дата до"
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <Button 
              variant="contained"
              onClick={fetchBonusLeads}
              disabled={loading}
              fullWidth={isMobile}
            >
              {loading ? 'Обновление...' : 'Обновить'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Уведомления о бонусах */}
      {notifications.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Уведомления о бонусах
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {notifications.map((notif, index) => (
                <Alert 
                  key={index} 
                  severity={notif.type === 'new_bonus' ? 'info' : 'success'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2">
                    {new Date(notif.created_at).toLocaleString()}: {notif.message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Таблица бонусных лидов */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Бонусные лиды ({bonusLeads.length})
          </Typography>
          
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLeads.length === bonusLeads.length && bonusLeads.length > 0}
                      onChange={toggleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 60 }}>ID</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Телефон</TableCell>
                  {!isMobile && <TableCell>Клиент</TableCell>}
                  {!isMobile && <TableCell>Менеджер</TableCell>}
                  <TableCell>Проект</TableCell>
                  <TableCell>Бонус</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 6 : 10} align="center">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                ) : bonusLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 6 : 10} align="center">
                      Бонусные лиды не найдены
                    </TableCell>
                  </TableRow>
                ) : (
                  bonusLeads.map(lead => (
                    <TableRow key={lead.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => toggleSelectLead(lead.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{lead.id}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{lead.phone}</TableCell>
                      {!isMobile && <TableCell>{lead.customer_name}</TableCell>}
                      {!isMobile && <TableCell>{lead.manager_name}</TableCell>}
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{lead.project_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={lead.bonus ? 'Да' : 'Нет'} 
                          color={lead.bonus ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          color={lead.bonus ? 'error' : 'success'}
                          onClick={() => updateBonusStatus(lead.id, !lead.bonus)}
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', p: '2px 4px' }}
                        >
                          {lead.bonus ? '-' : '+'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Детальная аналитика */}
      {metrics && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Детальная аналитика
            </Typography>
            
            {/* По менеджерам */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Бонусные лиды по менеджерам
              </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
              {metrics.bonus_by_manager.map((manager, index) => (
                <Card variant="outlined" key={index}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {manager.manager_name}
                    </Typography>
                    <Typography variant="body2">
                      Бонусных лидов: {manager.bonus_count}
                    </Typography>
                    <Typography variant="body2">
                      Конверсия: {typeof manager.conversion_rate === 'number' ? manager.conversion_rate.toFixed(1) : '0.0'}%
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            </Box>

            {/* По проектам */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Бонусные лиды по проектам
              </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
              {metrics.bonus_by_project.map((project, index) => (
                <Card variant="outlined" key={index}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {project.project_name}
                    </Typography>
                    <Typography variant="body2">
                      Бонусных лидов: {project.bonus_count}
                    </Typography>
                    <Typography variant="body2">
                      Конверсия: {typeof project.conversion_rate === 'number' ? project.conversion_rate.toFixed(1) : '0.0'}%
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}; 